import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { RotateCcw } from 'lucide-react'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import { useDeckResetPreview, useResetCardMastery, useResetDeckMastery } from '../../hooks/useLearning'

/**
 * The manual reset's two confirmations (ADR-098).
 *
 * Both actions are irreversible in effect — the answers that built a level cannot
 * be replayed — so each is a confirm step, never an undo toast. The deck confirm
 * names the number of cards it will change, read from the server when it opens,
 * and cannot be confirmed until that number is known; the server refuses the
 * reset if the number has moved since (409), and the dialog re-reads it.
 */

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status
}

function ResetDialog({
  title, message, error, confirmLabel, onCancel, onConfirm, confirmDisabled, cancelLabel,
}: {
  title: string
  message: string
  error: string | null
  confirmLabel: string | null
  cancelLabel: string
  onCancel: () => void
  onConfirm: () => void
  confirmDisabled: boolean
}) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const onCancelRef = useRef(onCancel)
  useEffect(() => { onCancelRef.current = onCancel })
  // ADR-106 P3: mounted only while open.
  useSwipeNavBlock(true, 'mastery-reset-dialog')
  useEffect(() => {
    // Focus lands on the safe choice, once, when the dialog opens.
    cancelRef.current?.focus()
    // Capture phase, and stopped: the deck page runs its own Escape ladder
    // (close menu → leave card → …), which must not also fire under a dialog.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      e.preventDefault()
      onCancelRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  const body: ReactNode = (
    <div
      onClick={(e) => { e.stopPropagation(); onCancel() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mastery-reset-title"
        aria-describedby="mastery-reset-msg"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
          padding: 28, maxWidth: 420, width: '100%', fontFamily: 'var(--font-ui)',
        }}
      >
        <h3 id="mastery-reset-title" style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
          {title}
        </h3>
        <p id="mastery-reset-msg" style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          {message}
        </p>
        {error && (
          <p role="alert" style={{ margin: '12px 0 0', fontSize: 13.5, color: 'var(--danger)' }}>{error}</p>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24, flexWrap: 'wrap' }}>
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              padding: '10px 18px', background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-pill)', fontSize: 14, cursor: 'pointer', color: 'var(--text-primary)',
            }}
          >
            {cancelLabel}
          </button>
          {confirmLabel !== null && (
            <button
              onClick={onConfirm}
              disabled={confirmDisabled}
              style={{
                padding: '10px 18px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none',
                borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600,
                cursor: confirmDisabled ? 'default' : 'pointer', opacity: confirmDisabled ? 0.6 : 1,
              }}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
  return createPortal(body, document.body)
}

/**
 * "I've forgotten this word" — offered on a studied card that is above the bottom
 * of the loop (a never-studied card, or one already at level 1, has nothing to
 * reset, so the control is absent rather than disabled).
 */
export function ForgotWordButton({
  cardId, deckId, term, masteryLevel, isMastered,
}: {
  cardId: string
  deckId: string
  term: string
  masteryLevel: number | null | undefined
  isMastered: boolean | undefined
}) {
  const { tr } = useLocale()
  const t = tr.masteryReset
  const [open, setOpen] = useState(false)
  const reset = useResetCardMastery(deckId)
  const [failed, setFailed] = useState(false)

  if (masteryLevel == null || (!isMastered && masteryLevel <= 1)) return null

  const close = () => { if (!reset.isPending) { setOpen(false); setFailed(false) } }
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
        aria-label={t.cardActionAria}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
          background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
          color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', fontSize: 13,
        }}
      >
        <RotateCcw size={13} strokeWidth={2.2} />
        {t.cardAction}
      </button>
      {open && (
        <ResetDialog
          title={t.cardTitle(term)}
          message={t.cardMsg}
          error={failed ? t.failed : null}
          cancelLabel={t.cancel}
          confirmLabel={reset.isPending ? t.confirming : t.confirm}
          confirmDisabled={reset.isPending}
          onCancel={close}
          onConfirm={() => {
            setFailed(false)
            reset.mutate(cardId, {
              onSuccess: () => setOpen(false),
              onError: () => setFailed(true),
            })
          }}
        />
      )}
    </>
  )
}

/** The deck-level confirm. Mounted only while open. */
export function DeckResetConfirm({
  deckId, onClose, onDone,
}: {
  deckId: string
  onClose: () => void
  onDone: (resetCount: number) => void
}) {
  const { tr } = useLocale()
  const t = tr.masteryReset
  const preview = useDeckResetPreview(deckId, true)
  const reset = useResetDeckMastery(deckId)
  const [error, setError] = useState<string | null>(null)

  const count = preview.data?.card_count
  const known = count !== undefined && !preview.isFetching
  const close = () => { if (!reset.isPending) onClose() }

  if (known && count === 0) {
    return (
      <ResetDialog
        title={t.deckNoneTitle}
        message={t.deckNoneMsg}
        error={null}
        cancelLabel={t.close}
        confirmLabel={null}
        confirmDisabled
        onCancel={close}
        onConfirm={() => {}}
      />
    )
  }

  return (
    <ResetDialog
      title={known ? t.deckTitle(count) : t.deckTitlePending}
      message={t.deckMsg}
      error={error ?? (preview.isError ? t.failed : null)}
      cancelLabel={t.cancel}
      confirmLabel={reset.isPending ? t.confirming : t.confirm}
      // An unknown count blocks confirmation: the learner confirms a number.
      confirmDisabled={!known || reset.isPending}
      onCancel={close}
      onConfirm={() => {
        if (!known) return
        setError(null)
        reset.mutate(count, {
          onSuccess: (data) => onDone(data.reset_count),
          onError: (err) => {
            if (statusOf(err) === 409) {
              setError(t.countChanged)
              preview.refetch()
            } else {
              setError(t.failed)
            }
          },
        })
      }}
    />
  )
}
